import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {Dispatch, SetStateAction, useState} from 'react';

import isArchived from 'archive/isArchived';
import {getBenchlingAppConfigurationQuery} from 'benchling-apps/configuration/__generated/getBenchlingAppConfigurationQuery';
import {RunSchemaDropdownQuery} from 'benchling-apps/interactivity/features/__generated/RunSchemaDropdownQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as modals from 'util/Modals';

import {ResourceFeatureAssociation} from './types';

type Feature = ExtractByTypename<
  getBenchlingAppConfigurationQuery['benchlingApp'],
  'BenchlingAppNode'
>['features'][number];

export const RUN_SCHEMA_DROPDOWN_QUERY: DocumentNode = gql`
  query RunSchemaDropdownQuery @skipAudit {
    ...AllAssaySchemasGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
`;

export interface RunSchemaDropdownProps {
  feature: Feature;
  appName: string;
  appId: string;
  resourceFeatureAssociations: Array<ResourceFeatureAssociation>;
  updateResourceFeatureAssociations: Dispatch<SetStateAction<Array<ResourceFeatureAssociation>>>;
}

export default function RunSchemaDropdown({
  feature,
  appName,
  appId,
  resourceFeatureAssociations,
  updateResourceFeatureAssociations,
}: RunSchemaDropdownProps): JSX.Element {
  const [searchString, setSearchString] = useState<string>('');

  function createNewAssociation(runSchemaId: string): ResourceFeatureAssociation {
    return {
      appSummary: {id: appId, name: appName},
      resourceId: runSchemaId,
      featureId: feature.id,
    };
  }

  function claimedByAppIds(runSchemaId: string): Array<string> {
    const rfas = resourceFeatureAssociations.filter((rfa) => rfa.resourceId === runSchemaId);
    return rfas.map((rfa) => rfa.appSummary.id);
  }

  function getDisabledMessage(runSchemaId: string): string {
    if (!claimedByAppIds(runSchemaId)) {
      return '';
    }
    const existingAssociationsForId = resourceFeatureAssociations.filter(
      (rfa) => rfa.resourceId === runSchemaId
    );
    const appNames = existingAssociationsForId.map((rfa) => rfa.appSummary.name);
    return `Run schema already in use by app ${appNames.join(', ')}`;
  }

  // Query for run schemas
  const schemasResult = useBenchlingQuery<RunSchemaDropdownQuery>(RUN_SCHEMA_DROPDOWN_QUERY);
  if (schemasResult.type === QueryResultType.ERROR) {
    notifications.error('Unable to load feature configuration options. Please try again.');
    return <></>;
  } else if (schemasResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const restRunSchemas = graphQLArrayToREST('RunSchema', schemasResult.data.runSchemas);
    const filteredRunSchemas = restRunSchemas.filter((schema) => !isArchived(schema));
    const runSchemaOptions = filteredRunSchemas.map((option) => ({
      name: option.name,
      value: option.name,
      runSchemaId: option.id,
      disabled: claimedByAppIds(option.id).length > 0,
      disabledMessage: getDisabledMessage(option.id),
    }));

    const dropdown = (
      <div className={css(styles.rowContainer)}>
        <FieldLabel isRequired={false} label={feature.name} labelStyle={FieldLabel.STYLES.LABEL} />
        <ChipSelectInput
          autoFocus={false}
          scrollable={true}
          isMulti={true}
          placeholder="Select run schema(s)"
          optionDatas={runSchemaOptions
            .filter(
              (runSchema) =>
                runSchema.name.toLowerCase().startsWith(searchString.toLowerCase()) &&
                !claimedByAppIds(runSchema.runSchemaId).includes(appId)
            )
            .sort((a, b) => Number(a.disabled) - Number(b.disabled))}
          query={searchString}
          value={getSelectedValues(resourceFeatureAssociations, feature.id, filteredRunSchemas)}
          onQueryChange={(substring) => {
            setSearchString(substring.target.value);
          }}
          onSelect={(value) => {
            const runSchemaId = filteredRunSchemas.filter((runInfo) => runInfo.name === value)[0].id;
            const newAssociation = createNewAssociation(runSchemaId);
            updateResourceFeatureAssociations([...resourceFeatureAssociations, newAssociation]);
            setSearchString('');
          }}
          onRemove={async (value) => {
            if (value === '(No access)') {
              return;
            }
            const confirm = await Promise.resolve(
              modals.confirm(
                'Remove run schema from feature configuration',
                `Removing this run schema will remove all canvases that are currently on runs of this schema. Are you sure you want to remove ${value}?`,
                ButtonStyle.NEGATIVE,
                {
                  promiseCompatible: true,
                  enableRedesign: true,
                }
              )
            );
            if (!confirm) {
              return;
            }
            const runSchemaId = filteredRunSchemas.filter((runSchema) => runSchema.name === value)[0].id;
            const newAssociations = resourceFeatureAssociations.filter(
              (rfa) => rfa.resourceId !== runSchemaId
            );
            updateResourceFeatureAssociations(newAssociations);
          }}
        />
      </div>
    );
    return dropdown;
  }
}

function getSelectedValues(
  associations: Array<ResourceFeatureAssociation>,
  featureId: number,
  runSchemas: Array<AssayRunSchema>
): Array<string> {
  const runSchemaIdsForFeature = associations
    .filter((rfa) => rfa.featureId === featureId)
    .map((rfa) => rfa.resourceId);
  let runSchemaNames = runSchemas
    .filter((schema) => runSchemaIdsForFeature.includes(schema.id))
    .map((schema) => schema.name);
  const numInaccessibleSchemas = runSchemaIdsForFeature.length - runSchemaNames.length;
  if (numInaccessibleSchemas > 0) {
    runSchemaNames = runSchemaNames.concat(
      Array(runSchemaIdsForFeature.length - runSchemaNames.length).fill('(No access)')
    );
  }
  return runSchemaNames;
}

const styles = StyleSheet.create({
  rowContainer: {
    marginTop: '30px',
  },
});
